/**
 * TelePortal Service
 *
 * API methods for TelePortal track and task management, documents and packages.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';
import type {
  HouseholdProposal,
  HouseholdScope,
  TaskStatus as TeleportalTaskStatus,
  TelePortalVisaPathwaysResponse,
} from '@tonnie/types';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export type TrackType = 'legal' | 'travel' | 'setup';
/**
 * A task's stored status — the database's set, declared once in `@tonnie/types`.
 * `overdue` is NOT a status (nothing ever stored it): lateness is the
 * server-derived `TelePortalTask.isOverdue`.
 */
export type TaskStatus = TeleportalTaskStatus;
export type DocumentStatus = 'uploaded' | 'validated' | 'pending' | 'rejected' | 'missing';
export type PackageType = 'visa_application' | 'work_permit' | 'residence' | 'custom';
export type ExtractionType = 'identity' | 'financial' | 'education' | 'full';

export interface TelePortalTrack {
  id: string;
  type: TrackType;
  name: string;
  description?: string;
  status: 'not_started' | 'in_progress' | 'completed';
  progressPercent: number;
  tasksTotal: number;
  tasksCompleted: number;
  tasksBlocked: number;
  startedAt?: string;
}

export interface TelePortalTracksResponse {
  tracks: TelePortalTrack[];
  overallProgress: number;
}

export interface TelePortalTask {
  id: string;
  trackId: string;
  trackType?: TrackType;
  taskCode?: string;
  taskType?: string;
  title: string;
  description?: string | null;
  status: TaskStatus;
  /** Past its due date and not settled — derived by the server from `dueDate` + `status`. */
  isOverdue: boolean;
  priority: 'high' | 'medium' | 'low';
  estimatedDuration?: string | null;
  isBlocking: boolean;
  /**
   * Who the task is for, as stored (`teleportal_tasks.household_scope`):
   * `talent_only` is the talent alone; the rest name the household.
   */
  householdScope: HouseholdScope;
  dependsOn?: string[] | null;
  milesReward: number;
  dueDate?: string | null;
  completedAt?: string | null;
  metadata?: Record<string, unknown> | null;
  ownershipMode?: string;
  isCompanyTask?: boolean;
  householdMemberId?: string | null;
  templateId?: string | null;
  milestoneId?: string | null;
  assignedTo?: string | null;
  notes?: string | null;
  location?: 'home_country' | 'target_country' | 'either' | 'online' | null;
  /**
   * Server-authoritative freemium lock. `locked` is true on a basic-tier (free)
   * journey for tasks outside the free preview slice (first milestone per track);
   * `lockReason` is `'billing'` — unlock the journey in web billing. Distinct from
   * `status: 'blocked'`, which is a task-dependency signal, not an entitlement.
   */
  locked?: boolean;
  lockReason?: 'billing' | null;
}

/**
 * A roster row, the applicant included.
 *
 * `clientRef` is the member's stable identity — the thing a per-member task
 * code is built from — and `isSelf` marks the applicant's own row. Before
 * those existed, "the applicant" was encoded as the ABSENCE of a member id,
 * which is also how "the whole household" was encoded, and the checklist
 * engine could not iterate an absence: seven templates that name the applicant
 * as recipient expanded to nothing on every journey ever generated.
 */
export interface TeleportalHouseholdMember {
  id: string;
  clientRef: string | null;
  type: string;
  isSelf: boolean;
  name: string;
  firstName: string | null;
  lastName: string | null;
  nationality?: string | null;
  dateOfBirth?: string | null;
  schoolStage?: string | null;
  profession?: string | null;
  petSpecies?: string | null;
  petBreed?: string | null;
  relationship?: string | null;
  dependencyStatus?: string | null;
  /** F35 — "has a valid passport?" as the citizen answered it. `null` = not answered. */
  hasPassport: boolean | null;
  /** F35 — "vaccinations up to date?" as the citizen answered it. `null` = not answered. */
  vaccinationOk: boolean | null;
  orderIndex: number;
  isActive: boolean;
}

export interface TelePortalDocument {
  id: string;
  journeyInstanceId: string;
  trackId?: string | null;
  taskId?: string | null;
  vaultFileId?: string | null;
  documentType: string;
  /**
   * Display prose in the viewer's locale, resolved by the server (W8.B).
   *
   * A server-created placeholder's name is the rendered
   * `teleportal.docType.<type>`; a citizen's upload keeps the name they typed.
   * It is prose on both paths, so it can be printed as it comes. Prefer
   * `documentNameKey` when it is non-null.
   */
  documentName: string;
  /**
   * The i18n key behind `documentName`, or null when the name is the citizen's
   * own prose. Resolve it with your own `t()` so a key your bundle lacks
   * renders raw (loudly) rather than silently showing the server's English.
   */
  documentNameKey: string | null;
  description?: string | null;
  validationStatus: DocumentStatus;
  tonnieChecked: boolean;
  fileSize?: number | null;
  mimeType?: string | null;
  expiryDate?: string | null;
  uploadedAt: string;
  validatedAt?: string | null;
  validationNotes?: string | null;
  householdMemberId?: string | null;
  location?: string | null;
  metadata?: Record<string, unknown> | null;
  validityDays?: number | null;
  requiresCertification: boolean;
  certificationType?: string | null;
}

export interface TelePortalPackage {
  id: string;
  name: string;
  description?: string;
  packageType: PackageType;
  status: 'generating' | 'completed' | 'failed';
  documentCount: number;
  documents?: Array<{
    id: string;
    name: string;
    type: string;
    status: DocumentStatus;
  }>;
  journeyId: string;
  createdAt: string;
  completedAt?: string;
  downloadUrl?: string;
  expiresAt?: string;
}

export interface TelePortalMilestone {
  id: string;
  title: string;
  track: TrackType;
  targetDate?: string;
  completedDate?: string;
  isCompleted: boolean;
  tasksRequired: number;
  tasksCompleted: number;
  orderIndex?: number;
}

// ═══════════════════════════════════════════════════════════════════════
// INPUT TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface GetTasksParams {
  track?: TrackType;
  status?: TaskStatus;
  location?: 'home_country' | 'target_country' | 'either' | 'online';
  [key: string]: string | undefined;
}

export interface UpdateTaskInput {
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface CompleteTaskInput {
  completionNotes?: string;
  documentIds?: string[];
}

export interface UploadDocumentInput {
  fileId: string;
  taskId?: string;
  trackId?: string;
  documentType: string;
  documentName: string;
  description?: string;
  mimeType?: string;
  fileSize?: number;
}

export interface GeneratePackageInput {
  name: string;
  description?: string;
  documentIds: string[];
  packageType: PackageType;
}

export interface SharePackageInput {
  recipientEmail: string;
  expiresInDays?: number;
  message?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface CompleteTaskResponse {
  taskId: string;
  status: 'completed';
  completedAt: string;
  milesEarned: number;
  nextTasks: TelePortalTask[];
}

export interface ValidateDocumentResponse {
  documentId: string;
  validationStatus: DocumentStatus;
  validatedAt: string;
  issues: string[];
  confidence: number;
  extractedData?: Record<string, unknown>;
}

export interface SharePackageResponse {
  shareId: string;
  packageId: string;
  recipientEmail: string;
  shareUrl: string;
  expiresAt: string;
  message?: string;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class TelePortalService {
  constructor(private readonly client: TonnieClient) {}

  // ═══════════════════════════════════════════════════════════════════════
  // TRACKS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Get all tracks for a journey
   */
  async getTracks(journeyId: string): Promise<TelePortalTracksResponse> {
    return this.client.get<TelePortalTracksResponse>(API_ENDPOINTS.TELEPORTAL.TRACKS(journeyId));
  }

  /**
   * Get specific track details
   */
  async getTrack(
    journeyId: string,
    trackType: TrackType
  ): Promise<TelePortalTrack & { tasks: TelePortalTask[] }> {
    return this.client.get<TelePortalTrack & { tasks: TelePortalTask[] }>(
      API_ENDPOINTS.TELEPORTAL.TRACK(journeyId, trackType)
    );
  }

  /**
   * Get milestones for a journey (one per track; completion from track status and task counts)
   */
  async getMilestones(journeyId: string): Promise<TelePortalMilestone[]> {
    return this.client.get<TelePortalMilestone[]>(API_ENDPOINTS.TELEPORTAL.MILESTONES(journeyId));
  }

  // ═══════════════════════════════════════════════════════════════════════
  // TASKS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Get all tasks across tracks
   */
  async getTasks(journeyId: string, params?: GetTasksParams): Promise<TelePortalTask[]> {
    return this.client.get<TelePortalTask[]>(API_ENDPOINTS.TELEPORTAL.TASKS(journeyId), { params });
  }

  /**
   * Get task details
   */
  async getTask(journeyId: string, taskId: string): Promise<TelePortalTask> {
    return this.client.get<TelePortalTask>(API_ENDPOINTS.TELEPORTAL.TASK(journeyId, taskId));
  }

  /**
   * Update task
   */
  async updateTask(
    journeyId: string,
    taskId: string,
    input: UpdateTaskInput
  ): Promise<{ taskId: string; updated: boolean; metadata?: Record<string, unknown> }> {
    return this.client.patch(API_ENDPOINTS.TELEPORTAL.TASK(journeyId, taskId), input);
  }

  /**
   * Complete task
   */
  async completeTask(
    journeyId: string,
    taskId: string,
    input?: CompleteTaskInput
  ): Promise<CompleteTaskResponse> {
    return this.client.post<CompleteTaskResponse>(
      API_ENDPOINTS.TELEPORTAL.TASK_COMPLETE(journeyId, taskId),
      input ?? {}
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // DOCUMENTS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Get all documents for a journey
   */
  async getDocuments(journeyId: string): Promise<TelePortalDocument[]> {
    return this.client.get<TelePortalDocument[]>(API_ENDPOINTS.TELEPORTAL.DOCUMENTS(journeyId));
  }

  /**
   * Upload document
   */
  async uploadDocument(journeyId: string, input: UploadDocumentInput): Promise<TelePortalDocument> {
    return this.client.post<TelePortalDocument>(
      API_ENDPOINTS.TELEPORTAL.DOCUMENTS(journeyId),
      input
    );
  }

  /**
   * Validate document
   */
  async validateDocument(journeyId: string, docId: string): Promise<ValidateDocumentResponse> {
    return this.client.post<ValidateDocumentResponse>(
      API_ENDPOINTS.TELEPORTAL.DOCUMENT_VALIDATE(journeyId, docId),
      {}
    );
  }

  /**
   * Delete document
   */
  async deleteDocument(journeyId: string, documentId: string): Promise<{ deleted: boolean }> {
    return this.client.delete(API_ENDPOINTS.TELEPORTAL.DOCUMENT(journeyId, documentId));
  }

  /**
   * Get presigned download URL for document
   */
  async getDocumentDownloadUrl(
    journeyId: string,
    documentId: string
  ): Promise<{ downloadUrl: string; expiresAt: string }> {
    return this.client.get(API_ENDPOINTS.TELEPORTAL.DOCUMENT_DOWNLOAD(journeyId, documentId));
  }

  /** BL-090 — download the selected documents as a single ZIP blob. */
  async downloadDocumentsZip(journeyId: string, documentIds: string[]): Promise<Blob> {
    return this.client.post(
      API_ENDPOINTS.TELEPORTAL.DOCUMENTS_DOWNLOAD_ZIP(journeyId),
      { documentIds },
      { responseType: 'blob' }
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PACKAGES
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Generate application package
   */
  async generatePackage(
    journeyId: string,
    input: GeneratePackageInput
  ): Promise<TelePortalPackage> {
    return this.client.post<TelePortalPackage>(
      `${API_ENDPOINTS.TELEPORTAL.PACKAGES(journeyId).replace('/packages', '/package')}`,
      input
    );
  }

  /**
   * Get all packages for a journey
   */
  async getPackages(journeyId: string): Promise<{ packages: TelePortalPackage[]; total: number }> {
    return this.client.get(API_ENDPOINTS.TELEPORTAL.PACKAGES(journeyId));
  }

  /**
   * Get package details
   */
  async getPackage(journeyId: string, packageId: string): Promise<TelePortalPackage> {
    return this.client.get<TelePortalPackage>(
      API_ENDPOINTS.TELEPORTAL.PACKAGE(journeyId, packageId)
    );
  }

  /**
   * Share package with recipient
   */
  async sharePackage(
    journeyId: string,
    packageId: string,
    input: SharePackageInput
  ): Promise<SharePackageResponse> {
    return this.client.post<SharePackageResponse>(
      API_ENDPOINTS.TELEPORTAL.PACKAGE_SHARE(journeyId, packageId),
      input
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SETUP PROFILE
  // ═══════════════════════════════════════════════════════════════════════

  async getSetupProfile(journeyId: string) {
    return this.client.get(API_ENDPOINTS.TELEPORTAL.SETUP(journeyId));
  }

  async saveSetupProfile(
    journeyId: string,
    dimensions: Record<string, unknown>,
    origin: string,
    destination: string,
    hasSponsor: boolean
  ) {
    return this.client.post(API_ENDPOINTS.TELEPORTAL.SETUP(journeyId), {
      dimensions,
      origin,
      destination,
      hasSponsor,
    });
  }

  /**
   * Activate a pre-generated Relocation Kit (no second Brain call).
   * Used after the guide flow generates a kit and the user accepts it.
   */
  async activateKit(
    journeyId: string,
    data: {
      dimensions: Record<string, unknown>;
      origin: string;
      destination: string;
      hasSponsor: boolean;
      kit: Record<string, unknown>;
      modifications?: { removed_task_codes: string[] };
    }
  ) {
    return this.client.post(API_ENDPOINTS.TELEPORTAL.SETUP_ACTIVATE(journeyId), data);
  }

  async refreshSetup(
    journeyId: string,
    dimensions: Record<string, unknown>,
    origin: string,
    destination: string,
    hasSponsor: boolean
  ) {
    return this.client.post(API_ENDPOINTS.TELEPORTAL.SETUP_REFRESH(journeyId), {
      dimensions,
      origin,
      destination,
      hasSponsor,
    });
  }

  async getPrePopulate(journeyId: string) {
    return this.client.get(API_ENDPOINTS.TELEPORTAL.SETUP_PRE_POPULATE(journeyId));
  }

  /**
   * Instantiate a Relocation Kit from collected dimensions (Brain.Learning).
   * Used by the Setup Guide processing step before showing the kit to the user.
   */
  async instantiateKit(
    journeyId: string,
    dimensions: Record<string, unknown>,
    origin: string,
    destination: string,
    hasSponsor: boolean
  ) {
    return this.client.post(
      API_ENDPOINTS.TELEPORTAL.SETUP_INSTANTIATE(journeyId),
      {
        dimensions,
        origin,
        destination,
        hasSponsor,
      },
      { timeout: 120_000 }
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // HOUSEHOLD MEMBERS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * The roster. Pass `includeSelf` to get the applicant's own row too.
   *
   * It is opt-in because the phone's TelePortal screen adds its own applicant
   * entry and reads a non-empty roster as "this citizen has a family". A caller
   * that draws the applicant from the roster (`isSelf`) asks.
   */
  async getHouseholdMembers(journeyId: string, opts?: { includeSelf?: boolean }) {
    return this.client.get<TeleportalHouseholdMember[]>(
      API_ENDPOINTS.TELEPORTAL.HOUSEHOLD_MEMBERS(journeyId),
      opts?.includeSelf ? { params: { includeSelf: true } } : {}
    );
  }

  /**
   * The visa pathways the setup step offers for this journey's destination
   * (F38) — the brain catalog's list, labels as i18n keys.
   */
  async getVisaPathways(journeyId: string) {
    return this.client.get<TelePortalVisaPathwaysResponse>(
      API_ENDPOINTS.TELEPORTAL.SETUP_VISA_PATHWAYS(journeyId)
    );
  }

  /**
   * What the household step pre-fills (F35 · D-17): the saved roster, else the
   * citizen's onboarding family answer PROPOSED (never saved until they confirm
   * the step), else nothing. `source` says which.
   */
  async getHouseholdProposal(journeyId: string) {
    return this.client.get<HouseholdProposal>(
      API_ENDPOINTS.TELEPORTAL.HOUSEHOLD_PROPOSAL(journeyId)
    );
  }

  async createHouseholdMember(journeyId: string, member: Record<string, unknown>) {
    return this.client.post(API_ENDPOINTS.TELEPORTAL.HOUSEHOLD_MEMBERS(journeyId), member);
  }

  async updateHouseholdMember(journeyId: string, memberId: string, data: Record<string, unknown>) {
    return this.client.patch(API_ENDPOINTS.TELEPORTAL.HOUSEHOLD_MEMBER(journeyId, memberId), data);
  }

  async archiveHouseholdMember(journeyId: string, memberId: string) {
    return this.client.delete(API_ENDPOINTS.TELEPORTAL.HOUSEHOLD_MEMBER(journeyId, memberId));
  }

  // ═══════════════════════════════════════════════════════════════════════
  // TASK REOPEN
  // ═══════════════════════════════════════════════════════════════════════

  async reopenTask(journeyId: string, taskId: string, reason?: string) {
    return this.client.post(API_ENDPOINTS.TELEPORTAL.TASK_REOPEN(journeyId, taskId), {
      ...(reason ? { reason } : {}),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // DOCUMENT VISION
  // ═══════════════════════════════════════════════════════════════════════

  async getDocumentVisionResult(journeyId: string, documentId: string) {
    return this.client.get(API_ENDPOINTS.TELEPORTAL.DOCUMENT_VISION(journeyId, documentId));
  }
}

export function createTelePortalService(client: TonnieClient): TelePortalService {
  return new TelePortalService(client);
}
